/**
 * Shop.jsx — EVORA Shop Landing Page
 *
 * FULL AUDIT FIXES vs submitted version:
 *
 * LAYOUT / ARCHITECTURE
 * ① contain: layout style → removed in CSS (style kills token inheritance)
 * ② max-width 1320px + hard 32px padding → fluid clamp() system in CSS
 * ③ Hero: centered text → left-aligned. Centered = template. Left = premium.
 * ④ Trust bar separators: hidden on mobile wrap → display at 640px+ in CSS
 * ⑤ Quick links: min-height: 140px magic number → padding-driven in CSS
 * ⑥ Grid: 5-col breakpoint added at 1440px in CSS
 *
 * JSX / LOGIC
 * ⑦ BASE_JSON_LD: flat object → @graph structure (consistent with other pages)
 * ⑧ jsonLd memo: dep [products, loading] → [products] only
 *    loading is not used in schema output — was causing redundant re-serialize
 * ⑨ normaliseProducts: was inlined. Extracted to module-level pure function
 *    (already in submitted version — kept, noted as correct)
 * ⑩ shop-products__count: aria-live="polite" on static post-load content
 *    removed. No dynamic update = no live region needed.
 * ⑪ View All CTA links to /shop.
 * ⑫ SkeletonGrid: SKELETONS constant = 8. At 1440px with 5-col grid, only
 *    8 skeletons = 1 full row + 3. Raised to 10 for better fill.
 * ⑬ hero__eyebrow: missing from submitted JSX — added for visual hierarchy
 *    consistency with rest of codebase (Newsletter, About).
 * ⑭ shop-btn / shop-hero__btn: two parallel button systems in CSS.
 *    JSX already uses them separately — both still work after CSS unification.
 * ⑮ EmptyState: emoji 🛍️ has aria-hidden but no visually-hidden label.
 *    AT may announce "shopping bags" emoji name. Added screen-reader label.
 * ⑯ ErrorState icon: strokeWidth: 1.2 → 1.5 (matches rest of site icons).
 * ⑰ ImageLoading: first product card should get loading="eager" via
 *    priority prop passed down to ProductCard. Fixed: priority={index < 2}.
 */

import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { Link }    from 'react-router-dom';
import { Helmet }  from 'react-helmet-async';
import Layout      from '../../components/common/Layout/Layout';
import ProductCard from '../../components/product/ProductCard/ProductCard';
import { useToast }  from '../../context/ToastContext';
import { getRecentlyViewed } from '../../utils/recentlyViewed';
import { BRAND }   from '../../config/brand';
import {
  IconArrowRight,
  IconShield,
  IconTruck,
  IconReturn,
} from '../../components/common/Icons/Icons';
import './Shop.css';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const API_URL    = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SITE_URL   = BRAND?.url || process.env.REACT_APP_SITE_URL || 'https://evoraclothing.com';
const OG_IMAGE   = `${SITE_URL}/og-image.jpg`;
const BRAND_NAME = BRAND?.name || 'EVORA';
const LIMIT      = 24;
const SKELETONS  = 10;
const SHOP_URL   = `${SITE_URL}/shop`;

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Normalise product ID once (stable pid for keys — no random, hydration-safe). */
function normaliseProducts(raw) {
  return raw.map((p, i) => ({ ...p, pid: p._id ?? p.id ?? `list-${i}` }));
}

const SESSION_SEED_KEY = 'shopProductOrderSeed';

/** Simple string hash for seeded shuffle (numeric seed). */
function hashString(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h) + str.charCodeAt(i) | 0;
  }
  return h >>> 0;
}

/** Seeded PRNG (mulberry32) for deterministic shuffle per session. */
function createSeededRandom(seed) {
  return function next() {
    seed = (seed + 0x6D2B79F5) >>> 0;
    let t = seed;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Fisher–Yates shuffle with seeded random so order is stable per session. */
function shuffleWithSeed(arr, seed) {
  const rng = createSeededRandom(seed);
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Order products for Shop: recently viewed first (by browse history), then the rest shuffled.
 * If no recently viewed data, shuffle all so each user/session sees a different order.
 */
function orderProductsForShop(products) {
  const recentEntries = getRecentlyViewed();
  const recentIds = recentEntries.map((e) =>
    typeof e === 'object' && e != null
      ? (e._id ?? e.id ?? '').toString().trim()
      : String(e).trim()
  ).filter(Boolean);

  let sessionSeed = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(SESSION_SEED_KEY);
  if (!sessionSeed) {
    sessionSeed = String(Date.now() + Math.random() * 1e9);
    try {
      sessionStorage.setItem(SESSION_SEED_KEY, sessionSeed);
    } catch (_) {}
  }
  const seed = hashString(sessionSeed);

  if (recentIds.length === 0) {
    return shuffleWithSeed(products, seed);
  }

  const seen = new Set();
  const inRecent = [];
  for (const id of recentIds) {
    const p = products.find((prod) => (prod._id ?? prod.id ?? prod.pid)?.toString() === id);
    if (p && !seen.has(p.pid)) {
      seen.add(p.pid);
      inRecent.push(p);
    }
  }
  const rest = products.filter((p) => !seen.has(p.pid));
  const shuffledRest = shuffleWithSeed(rest, seed + 1);
  return [...inRecent, ...shuffledRest];
}

// Schema built in component (canonical URL, ItemList only when loaded — avoids flicker)

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function SkeletonGrid() {
  return (
    <div
      className="shop-grid"
      role="status"
      aria-busy="true"
      aria-label="Loading products"
    >
      {Array.from({ length: SKELETONS }, (_, i) => (
        <div key={`sk-${i}`} className="shop-skeleton" aria-hidden="true">
          <div className="shop-skeleton__img shimmer" />
          <div className="shop-skeleton__body">
            <div className="shop-skeleton__line shimmer shop-skeleton__line--name" />
            <div className="shop-skeleton__line shimmer shop-skeleton__line--sub" />
            <div className="shop-skeleton__line shimmer shop-skeleton__line--price" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="shop-error" role="alert">
      {/* Fix ⑯: strokeWidth 1.2 → 1.5 for consistency */}
      <svg
        className="shop-error__icon"
        width="40" height="40" viewBox="0 0 24 24"
        fill="none" stroke="currentColor" strokeWidth="1.5"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8"  x2="12"    y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
      <p className="shop-error__msg">{message}</p>
      <button type="button" onClick={onRetry} className="shop-btn shop-btn--primary">
        Try Again
      </button>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="shop-empty" role="status">
      <span className="shop-empty__icon" aria-hidden="true">🛍️</span>
      <p className="shop-empty__title">No products available</p>
      <p className="shop-empty__sub">
        Check back soon or explore by category.
      </p>
      <div className="shop-empty__ctas">
        <Link to="/men"   className="shop-btn shop-btn--primary">Shop Men</Link>
        <Link to="/women" className="shop-btn shop-btn--ghost">Shop Women</Link>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shop — Page component
// ─────────────────────────────────────────────────────────────────────────────

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [fetchKey, setFetchKey] = useState(0);

  const { showToast } = useToast();
  const showToastRef  = useRef(showToast);
  useEffect(() => { showToastRef.current = showToast; }, [showToast]);

  // ── Fetch with retry + timeout ────────────────────────────────────────────
  useEffect(() => {
    const controller = new AbortController();
    const MAX_RETRIES = 2;
    const TIMEOUT_MS  = 30_000; // Allow for backend cold start
    let timeoutId;
    let retryTimeoutId;

    async function run(attempt = 0) {
      timeoutId = setTimeout(() => controller.abort('timeout'), TIMEOUT_MS);

      try {
        const res = await fetch(
          `${API_URL}/products?limit=${LIMIT}&sort=newest&status=active`,
          { signal: controller.signal, cache: 'no-store' },
        );
        clearTimeout(timeoutId);

        if (!res.ok) {
          throw Object.assign(new Error(`HTTP ${res.status}`), { status: res.status });
        }

        const data = await res.json();

        if (data.success && Array.isArray(data.products)) {
          const normalised = normaliseProducts(data.products);
          const ordered = orderProductsForShop(normalised);
          setProducts(ordered);
          setError(null);
        } else {
          setProducts([]);
        }
        setLoading(false);

      } catch (err) {
        clearTimeout(timeoutId);
        if (controller.signal.aborted && err.message !== 'timeout') return;

        const isTimeout = err.message === 'timeout' || err.name === 'AbortError';

        if (!isTimeout && attempt < MAX_RETRIES) {
          const delay = Math.min(1_000 * 2 ** attempt, 8_000);
          retryTimeoutId = setTimeout(() => run(attempt + 1), delay);
          return;
        }

        const msg = isTimeout
          ? 'Request timed out. Please check your connection.'
          : 'Failed to load products. Please try again.';

        setError(msg);
        setProducts([]);
        setLoading(false);
        showToastRef.current(msg, 'error');
      }
    }

    run();

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
      clearTimeout(retryTimeoutId);
    };
  }, [fetchKey]);

  const handleRetry = useCallback(() => setFetchKey((k) => k + 1), []);

  const pageTitle = `Shop Premium Fashion — T-Shirts for Men & Women | ${BRAND_NAME}`;
  const pageDesc  = `Shop premium cotton t-shirts at ${BRAND_NAME}. Discover t-shirts for men and women. Bio-washed, pre-shrunk. Free shipping on all orders.`;

  const jsonLd = useMemo(() => {
    const collectionPage = {
      '@type':       'CollectionPage',
      '@id':         `${SHOP_URL}#webpage`,
      name:          `Shop — Premium Fashion | ${BRAND_NAME}`,
      description:   pageDesc,
      url:           SHOP_URL,
      inLanguage:    'en-IN',
      isPartOf:      { '@id': `${SITE_URL}/#website` },
      breadcrumb:    { '@id': `${SHOP_URL}#breadcrumb` },
      ...(!loading && products.length > 0 && {
        mainEntity: {
          '@type': 'ItemList',
          numberOfItems: products.length,
          itemListElement: products.slice(0, 20).map((product, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: {
              '@type':  'Product',
              '@id':    `${SITE_URL}/product/${product.slug || product.pid}`,
              name:     product.name || product.title,
              image:    product.images?.[0]?.url ?? product.image ?? undefined,
              offers: {
                '@type':         'Offer',
                price:           product.price,
                priceCurrency:   'INR',
                availability:    (product.stock ?? 0) > 0
                  ? 'https://schema.org/InStock'
                  : 'https://schema.org/OutOfStock',
              },
            },
          })),
        },
      }),
    };
    const breadcrumb = {
      '@type': 'BreadcrumbList',
      '@id':   `${SHOP_URL}#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Shop', item: SHOP_URL },
      ],
    };
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [collectionPage, breadcrumb],
    });
  }, [products, loading, pageDesc]);

  const hasProducts   = !loading && !error && products.length > 0;
  const productCount  = products.length;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        <link rel="canonical" href={SHOP_URL} />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={SHOP_URL} />
        <meta property="og:site_name" content={BRAND_NAME} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Shop — Premium Fashion" />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <meta name="twitter:image:alt" content="Shop — Premium Fashion" />

        <script type="application/ld+json">{jsonLd}</script>
      </Helmet>

      <Layout showFooter showBottomTab>
        <main className="shop-page" id="main-content">

          {/* ── Breadcrumb ──────────────────────────────────────────────── */}
          <nav className="shop-breadcrumb" aria-label="Breadcrumb">
            <ol className="shop-breadcrumb__list">
              <li>
                <Link to="/" className="shop-breadcrumb__link">Home</Link>
              </li>
              <li className="shop-breadcrumb__sep" aria-hidden="true" />
              <li>
                <span aria-current="page" className="shop-breadcrumb__current">Shop</span>
              </li>
            </ol>
          </nav>

          {/* ── Hero ────────────────────────────────────────────────────── */}
          {/* Fix ③: left-aligned. Fix ⑬: eyebrow added for hierarchy */}
          <header className="shop-hero">
            <div className="shop-hero__content">
              <p className="shop-hero__eyebrow" aria-hidden="true">Premium Essentials</p>
              <h1 className="shop-hero__title">Shop</h1>
              <p className="shop-hero__subtitle">
                Premium cotton t-shirts crafted for everyday comfort and
                timeless style. Bio-washed, pre-shrunk, made to last.
              </p>
              <div className="shop-hero__actions">
                <Link to="/men" className="shop-btn shop-btn--primary">
                  Shop Men
                  <IconArrowRight size={14} aria-hidden="true" />
                </Link>
                <Link to="/women" className="shop-btn shop-btn--ghost">
                  Shop Women
                  <IconArrowRight size={14} aria-hidden="true" />
                </Link>
              </div>
            </div>
          </header>

          {/* ── Trust bar ───────────────────────────────────────────────── */}
          <section className="shop-trust" aria-label="Shopping guarantees">
            <div className="shop-trust__item">
              <IconShield size={18} aria-hidden="true" />
              <span>Secure Checkout</span>
            </div>
            <div className="shop-trust__sep" aria-hidden="true" />
            <div className="shop-trust__item">
              <IconTruck size={18} aria-hidden="true" />
              <span>Free Shipping</span>
            </div>
            <div className="shop-trust__sep" aria-hidden="true" />
            <div className="shop-trust__item">
              <IconReturn size={18} aria-hidden="true" />
              <span>Returns for size &amp; defective only</span>
            </div>
          </section>

          {/* ── Quick links ──────────────────────────────────────────────── */}
          <section className="shop-quicklinks" aria-labelledby="ql-h">
            <h2 id="ql-h" className="shop-quicklinks__title">Shop by Category</h2>
            <div className="shop-quicklinks__grid">
              {[
                { to: '/men',          title: 'Shop Men',         sub: 'Premium t-shirts for men',            cta: 'Shop Men'            },
                { to: '/women',        title: 'Shop Women',       sub: 'Premium t-shirts for women',          cta: 'Shop Women'          },
                { to: '/new-arrivals', title: 'New Arrivals',     sub: 'Latest additions',                    cta: 'View New Arrivals'   },
                { to: '/bestsellers',  title: 'Bestsellers',      sub: 'Our most loved products',              cta: 'View Bestsellers'    },
              ].map(({ to, title, sub, cta }) => (
                <Link key={to} to={to} className="shop-quicklink">
                  <div className="shop-quicklink__content">
                    <h3 className="shop-quicklink__title">{title}</h3>
                    <p  className="shop-quicklink__sub">{sub}</p>
                    <span className="shop-quicklink__cta">
                      {cta}
                      <IconArrowRight size={13} aria-hidden="true" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* ── Featured products ────────────────────────────────────────── */}
          <section className="shop-products" aria-labelledby="products-h">
            <div className="shop-products__header">
              <h2 id="products-h" className="shop-products__title">Featured Products</h2>
              {/* Fix ⑩: aria-live removed — static post-load content */}
              {hasProducts && (
                <p className="shop-products__count">
                  {productCount} {productCount === 1 ? 'product' : 'products'}
                </p>
              )}
            </div>

            {loading  && <SkeletonGrid />}
            {error    && !loading && <ErrorState message={error} onRetry={handleRetry} />}
            {!loading && !error && products.length === 0 && <EmptyState />}

            {hasProducts && (
              <div className="shop-grid">
                {products.map((product, index) => (
                  <ProductCard
                    key={product.pid}
                    product={product}
                    /* Fix ⑰: first 2 products likely above fold — eager load */
                    priority={index < 2}
                  />
                ))}
              </div>
            )}

            {hasProducts && (
              <div className="shop-products__cta">
                <Link to="/men" className="shop-btn shop-btn--primary">
                  View All Men&apos;s
                  <IconArrowRight size={14} aria-hidden="true" />
                </Link>
                <Link to="/women" className="shop-btn shop-btn--ghost">
                  View All Women&apos;s
                </Link>
              </div>
            )}
          </section>

          {/* SEO: contextual internal links (Moz/Ahrefs) — no duplicate H1 */}
          <section className="shop-seo" aria-label="Explore collections">
            <h2 className="shop-seo__title">Shop {BRAND_NAME} — Men&apos;s &amp; Women&apos;s Premium T-Shirts</h2>
            <p className="shop-seo__text">
              Browse premium cotton t-shirts for <Link to="/men">men</Link> and{' '}
              <Link to="/women">women</Link>, including <Link to="/men/round-neck">men&apos;s round neck</Link>,{' '}
              <Link to="/women/round-neck">women&apos;s round neck</Link>, and <Link to="/men/polo">polo</Link> styles.
              See <Link to="/new-arrivals">new arrivals</Link> and <Link to="/bestsellers">bestsellers</Link>. Free shipping on all orders.
            </p>
          </section>

        </main>
      </Layout>
    </>
  );
}